import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
  Loader2, ArrowLeft, Clock, Eye, CheckCircle, XCircle, User, MapPin,
  Briefcase, FileText, ExternalLink, CalendarDays, Send, Gift,
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';

type AppStatus = 'pending' | 'reviewed' | 'shortlisted' | 'interview_scheduled' | 'accepted' | 'rejected' | 'withdrawn';

interface ApplicationWithDetails {
  id: string;
  status: string;
  cover_message: string | null;
  company_message: string | null;
  created_at: string;
  updated_at: string;
  professional: {
    id: string; headline: string | null; bio: string | null;
    skills: string[] | null; sectors: string[] | null; languages: string[] | null;
    years_experience: number | null; linkedin_url: string | null; resume_url: string | null;
    user_id: string;
  };
  opportunity: { id: string; title: string; type: string; sector: string; };
}

interface ProfileInfo {
  first_name: string; last_name: string; email: string;
  country: string | null; is_diaspora: boolean;
}

const statusConfig: Record<string, { icon: typeof Clock; color: string; label: string }> = {
  pending: { icon: Clock, color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
  reviewed: { icon: Eye, color: 'bg-blue-100 text-blue-800', label: 'Reviewed' },
  shortlisted: { icon: CheckCircle, color: 'bg-primary/10 text-primary', label: 'Shortlisted' },
  interview_scheduled: { icon: CalendarDays, color: 'bg-purple-100 text-purple-800', label: 'Interview' },
  accepted: { icon: Gift, color: 'bg-success/10 text-success', label: 'Accepted' },
  rejected: { icon: XCircle, color: 'bg-destructive/10 text-destructive', label: 'Rejected' },
  withdrawn: { icon: XCircle, color: 'bg-muted text-muted-foreground', label: 'Withdrawn' },
};

export default function CompanyApplications() {
  const { user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [applications, setApplications] = useState<ApplicationWithDetails[]>([]);
  const [profiles, setProfiles] = useState<Record<string, ProfileInfo>>({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [opportunityFilter, setOpportunityFilter] = useState('all');
  const [selectedApp, setSelectedApp] = useState<ApplicationWithDetails | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);

  // Interview scheduling state
  const [showInterviewDialog, setShowInterviewDialog] = useState(false);
  const [interviewApp, setInterviewApp] = useState<ApplicationWithDetails | null>(null);
  const [interviewDate, setInterviewDate] = useState<Date | undefined>();
  const [interviewTime, setInterviewTime] = useState('10:00');
  const [interviewDuration, setInterviewDuration] = useState('30');
  const [interviewLocation, setInterviewLocation] = useState('');
  const [interviewLink, setInterviewLink] = useState('');
  const [interviewNotes, setInterviewNotes] = useState('');
  const [schedulingInterview, setSchedulingInterview] = useState(false);

  // Offer state
  const [showOfferDialog, setShowOfferDialog] = useState(false);
  const [offerApp, setOfferApp] = useState<ApplicationWithDetails | null>(null);
  const [offerMessage, setOfferMessage] = useState('');
  const [offerSalary, setOfferSalary] = useState('');
  const [offerStartDate, setOfferStartDate] = useState<Date | undefined>();
  const [offerConditions, setOfferConditions] = useState('');
  const [sendingOffer, setSendingOffer] = useState(false);

  useEffect(() => {
    if (!authLoading && (!user || role !== 'company')) { navigate('/auth'); return; }
    if (user) fetchApplications();
  }, [user, role, authLoading]);

  const fetchApplications = async () => {
    if (!user) return;
    try {
      const { data: company } = await supabase.from('companies').select('id').eq('user_id', user.id).single();
      if (!company) { setLoading(false); return; }

      const { data: opportunities } = await supabase.from('opportunities').select('id').eq('company_id', company.id);
      if (!opportunities || opportunities.length === 0) { setLoading(false); return; }

      const { data: apps, error } = await supabase
        .from('applications')
        .select(`id, status, cover_message, company_message, created_at, updated_at,
          professional:professionals(id, headline, bio, skills, sectors, languages, years_experience, linkedin_url, resume_url, user_id),
          opportunity:opportunities(id, title, type, sector)`)
        .in('opportunity_id', opportunities.map(o => o.id))
        .order('created_at', { ascending: false });

      if (error) throw error;
      const typedApps = (apps as unknown as ApplicationWithDetails[]) || [];
      setApplications(typedApps);

      const userIds = [...new Set(typedApps.map(a => a.professional?.user_id).filter(Boolean))];
      if (userIds.length > 0) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('user_id, first_name, last_name, email, country, is_diaspora')
          .in('user_id', userIds);
        if (profileData) {
          const map: Record<string, ProfileInfo> = {};
          profileData.forEach(p => { map[p.user_id] = p; });
          setProfiles(map);
        }
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally { setLoading(false); }
  };

  const updateStatus = async (applicationId: string, newStatus: AppStatus, message?: string) => {
    setUpdatingStatus(applicationId);
    try {
      const updateData: Record<string, unknown> = { status: newStatus };
      if (message) updateData.company_message = message;

      const { error } = await supabase.from('applications').update(updateData).eq('id', applicationId);
      if (error) throw error;

      setApplications(prev => prev.map(a => a.id === applicationId ? { ...a, status: newStatus, company_message: message || a.company_message } : a));
      if (selectedApp?.id === applicationId) setSelectedApp(prev => prev ? { ...prev, status: newStatus } : null);
      toast({ title: 'Status updated', description: `Application marked as ${newStatus.replace('_', ' ')}` });
    } catch (error) {
      console.error('Error updating status:', error);
      toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' });
    } finally { setUpdatingStatus(null); }
  };

  const scheduleInterview = async () => {
    if (!interviewApp || !interviewDate) return;
    setSchedulingInterview(true);
    try {
      const [hours, minutes] = interviewTime.split(':').map(Number);
      const scheduledAt = new Date(interviewDate);
      scheduledAt.setHours(hours, minutes, 0, 0);

      const { error: intError } = await supabase.from('interviews').insert({
        application_id: interviewApp.id,
        scheduled_at: scheduledAt.toISOString(),
        duration_minutes: parseInt(interviewDuration),
        location: interviewLocation || null,
        meeting_link: interviewLink || null,
        notes: interviewNotes || null,
      });
      if (intError) throw intError;

      await updateStatus(interviewApp.id, 'interview_scheduled',
        `Interview scheduled for ${format(scheduledAt, 'PPP')} at ${interviewTime}${interviewLocation ? ` at ${interviewLocation}` : ''}${interviewLink ? `. Meeting link: ${interviewLink}` : ''}`
      );

      setShowInterviewDialog(false);
      resetInterviewForm();
      toast({ title: 'Interview Scheduled', description: 'The applicant has been notified.' });
    } catch (error) {
      console.error('Error scheduling interview:', error);
      toast({ title: 'Error', description: 'Failed to schedule interview', variant: 'destructive' });
    } finally { setSchedulingInterview(false); }
  };

  const sendOffer = async () => {
    if (!offerApp || !offerMessage) return;
    setSendingOffer(true);
    try {
      const { error: offError } = await supabase.from('offers').insert({
        application_id: offerApp.id,
        message: offerMessage,
        salary_range: offerSalary || null,
        start_date: offerStartDate ? format(offerStartDate, 'yyyy-MM-dd') : null,
        conditions: offerConditions || null,
      });
      if (offError) throw offError;

      await updateStatus(offerApp.id, 'accepted', offerMessage);

      setShowOfferDialog(false);
      resetOfferForm();
      toast({ title: 'Offer Sent', description: 'The applicant has been notified of the offer.' });
    } catch (error) {
      console.error('Error sending offer:', error);
      toast({ title: 'Error', description: 'Failed to send offer', variant: 'destructive' });
    } finally { setSendingOffer(false); }
  };

  const resetInterviewForm = () => {
    setInterviewApp(null); setInterviewDate(undefined); setInterviewTime('10:00');
    setInterviewDuration('30'); setInterviewLocation(''); setInterviewLink(''); setInterviewNotes('');
  };

  const resetOfferForm = () => {
    setOfferApp(null); setOfferMessage(''); setOfferSalary('');
    setOfferStartDate(undefined); setOfferConditions('');
  };

  const openInterviewDialog = (app: ApplicationWithDetails) => {
    setInterviewApp(app); setShowInterviewDialog(true);
  };

  const openOfferDialog = (app: ApplicationWithDetails) => {
    setOfferApp(app); setShowOfferDialog(true);
  };

  const uniqueOpps = [...new Map(applications.map(a => [a.opportunity?.id, a.opportunity])).values()].filter(Boolean);
  const filtered = applications.filter(a => {
    if (statusFilter !== 'all' && a.status !== statusFilter) return false;
    if (opportunityFilter !== 'all' && a.opportunity?.id !== opportunityFilter) return false;
    return true;
  });
  const counts = applications.reduce((acc, a) => { acc[a.status] = (acc[a.status] || 0) + 1; return acc; }, {} as Record<string, number>);

  const getApplicantName = (app: ApplicationWithDetails) => {
    const p = profiles[app.professional?.user_id];
    return p ? `${p.first_name} ${p.last_name}` : 'Unknown';
  };

  if (authLoading || loading) {
    return <Layout><div className="container flex min-h-[50vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></Layout>;
  }

  const renderActions = (app: ApplicationWithDetails, size: 'sm' | 'default' = 'sm') => {
    const s = app.status;
    const disabled = updatingStatus === app.id;
    return (
      <div className="flex flex-wrap gap-2">
        {s === 'pending' && (
          <Button size={size} variant="outline" onClick={() => updateStatus(app.id, 'reviewed')} disabled={disabled}>
            <Eye className="h-3.5 w-3.5 mr-1" /> Review
          </Button>
        )}
        {(s === 'pending' || s === 'reviewed') && (
          <Button size={size} variant="outline" onClick={() => updateStatus(app.id, 'shortlisted')} disabled={disabled}>
            <CheckCircle className="h-3.5 w-3.5 mr-1" /> Shortlist
          </Button>
        )}
        {(s === 'shortlisted' || s === 'reviewed') && (
          <Button size={size} variant="outline" onClick={() => openInterviewDialog(app)} disabled={disabled}>
            <CalendarDays className="h-3.5 w-3.5 mr-1" /> Schedule Interview
          </Button>
        )}
        {(s === 'shortlisted' || s === 'interview_scheduled') && (
          <Button size={size} className="bg-success hover:bg-success/90 text-success-foreground" onClick={() => openOfferDialog(app)} disabled={disabled}>
            <Gift className="h-3.5 w-3.5 mr-1" /> Send Offer
          </Button>
        )}
        {s !== 'rejected' && s !== 'withdrawn' && s !== 'accepted' && (
          <Button size={size} variant="destructive" onClick={() => updateStatus(app.id, 'rejected')} disabled={disabled}>
            <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
          </Button>
        )}
      </div>
    );
  };

  return (
    <Layout>
      <div className="container py-8">
        <Button variant="ghost" className="mb-6 gap-2" onClick={() => navigate('/dashboard/company')}>
          <ArrowLeft className="h-4 w-4" /> Back to Dashboard
        </Button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold">Applications Inbox</h1>
          <p className="mt-1 text-muted-foreground">Review, interview, and manage applications for your opportunities.</p>
        </div>

        {/* Status Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          {Object.entries(statusConfig).map(([key, config]) => {
            const Icon = config.icon;
            return (
              <Card key={key} className={`cursor-pointer transition-all ${statusFilter === key ? 'ring-2 ring-primary' : ''}`}
                onClick={() => setStatusFilter(statusFilter === key ? 'all' : key)}>
                <CardContent className="flex items-center gap-3 p-3">
                  <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div>
                    <p className="text-xl font-bold">{counts[key] || 0}</p>
                    <p className="text-xs text-muted-foreground">{config.label}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <Select value={opportunityFilter} onValueChange={setOpportunityFilter}>
            <SelectTrigger className="w-64"><SelectValue placeholder="Filter by opportunity" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Opportunities</SelectItem>
              {uniqueOpps.map(opp => <SelectItem key={opp!.id} value={opp!.id}>{opp!.title}</SelectItem>)}
            </SelectContent>
          </Select>
          {(statusFilter !== 'all' || opportunityFilter !== 'all') && (
            <Button variant="ghost" size="sm" onClick={() => { setStatusFilter('all'); setOpportunityFilter('all'); }}>Clear filters</Button>
          )}
        </div>

        {/* Applications List */}
        {filtered.length === 0 ? (
          <Card><CardContent className="py-12 text-center">
            <p className="text-muted-foreground">{applications.length === 0 ? 'No applications received yet.' : 'No applications match the current filters.'}</p>
          </CardContent></Card>
        ) : (
          <div className="space-y-3">
            {filtered.map(app => {
              const config = statusConfig[app.status] || statusConfig.pending;
              const StatusIcon = config.icon;
              const profile = profiles[app.professional?.user_id];
              return (
                <Card key={app.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                      <div className="flex-1 space-y-2 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge className={config.color}><StatusIcon className="mr-1 h-3 w-3" />{config.label}</Badge>
                          {profile?.is_diaspora && <Badge variant="secondary">Diaspora</Badge>}
                        </div>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="font-semibold">{getApplicantName(app)}</span>
                          {app.professional?.headline && <span className="text-sm text-muted-foreground truncate">· {app.professional.headline}</span>}
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1"><Briefcase className="h-3 w-3" />{app.opportunity?.title}</span>
                          {profile?.country && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{profile.country}</span>}
                          <span>{format(new Date(app.created_at), 'MMM d, yyyy')}</span>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        {renderActions(app)}
                        <Button size="sm" variant="ghost" onClick={() => setSelectedApp(app)}>View</Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Applicant Detail Dialog */}
        <Dialog open={!!selectedApp} onOpenChange={(open) => !open && setSelectedApp(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            {selectedApp && (() => {
              const profile = profiles[selectedApp.professional?.user_id];
              const prof = selectedApp.professional;
              const config = statusConfig[selectedApp.status] || statusConfig.pending;
              const StatusIcon = config.icon;
              return (
                <>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary"><User className="h-5 w-5" /></div>
                      <div>
                        <p>{profile ? `${profile.first_name} ${profile.last_name}` : 'Applicant'}</p>
                        <p className="text-sm font-normal text-muted-foreground">{prof?.headline || 'No headline'}</p>
                      </div>
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-6 mt-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className={config.color}><StatusIcon className="mr-1 h-3 w-3" /> {config.label}</Badge>
                      <span className="text-sm text-muted-foreground">for <strong>{selectedApp.opportunity?.title}</strong></span>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div><p className="text-muted-foreground">Email</p><p className="font-medium">{profile?.email || '—'}</p></div>
                      <div><p className="text-muted-foreground">Country</p><p className="font-medium">{profile?.country || '—'}</p></div>
                      <div><p className="text-muted-foreground">Experience</p><p className="font-medium">{prof?.years_experience ? `${prof.years_experience} years` : '—'}</p></div>
                      <div><p className="text-muted-foreground">Diaspora</p><p className="font-medium">{profile?.is_diaspora ? 'Yes' : 'No'}</p></div>
                    </div>

                    {prof?.bio && <div><p className="text-sm font-medium mb-1">Bio</p><p className="text-sm text-muted-foreground">{prof.bio}</p></div>}
                    {selectedApp.cover_message && <div><p className="text-sm font-medium mb-1">Cover Message</p><p className="text-sm text-muted-foreground bg-muted p-3 rounded-lg">{selectedApp.cover_message}</p></div>}

                    {prof?.skills && (prof.skills as string[]).length > 0 && (
                      <div><p className="text-sm font-medium mb-2">Skills</p>
                        <div className="flex flex-wrap gap-1.5">{(prof.skills as string[]).map(s => <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>)}</div>
                      </div>
                    )}

                    {prof?.languages && (prof.languages as string[]).length > 0 && (
                      <div><p className="text-sm font-medium mb-2">Languages</p>
                        <div className="flex flex-wrap gap-1.5">{(prof.languages as string[]).map(l => <Badge key={l} variant="outline" className="text-xs">{l}</Badge>)}</div>
                      </div>
                    )}

                    <div className="flex gap-3">
                      {prof?.linkedin_url && <Button variant="outline" size="sm" asChild><a href={prof.linkedin_url} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-3.5 w-3.5 mr-1" /> LinkedIn</a></Button>}
                      {prof?.resume_url && <Button variant="outline" size="sm" asChild><a href={prof.resume_url} target="_blank" rel="noopener noreferrer"><FileText className="h-3.5 w-3.5 mr-1" /> Resume</a></Button>}
                    </div>

                    <div className="pt-2 border-t">{renderActions(selectedApp)}</div>
                    <p className="text-xs text-muted-foreground">Applied {format(new Date(selectedApp.created_at), 'MMM d, yyyy \'at\' h:mm a')}</p>
                  </div>
                </>
              );
            })()}
          </DialogContent>
        </Dialog>

        {/* Interview Scheduling Dialog */}
        <Dialog open={showInterviewDialog} onOpenChange={(open) => { if (!open) { setShowInterviewDialog(false); resetInterviewForm(); } }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><CalendarDays className="h-5 w-5 text-primary" /> Schedule Interview</DialogTitle>
            </DialogHeader>
            {interviewApp && (
              <div className="space-y-4 mt-2">
                <p className="text-sm text-muted-foreground">
                  Scheduling interview with <strong>{getApplicantName(interviewApp)}</strong> for <strong>{interviewApp.opportunity?.title}</strong>
                </p>

                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !interviewDate && "text-muted-foreground")}>
                        <CalendarDays className="mr-2 h-4 w-4" />
                        {interviewDate ? format(interviewDate, 'PPP') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={interviewDate} onSelect={setInterviewDate}
                        disabled={(date) => date < new Date()} initialFocus className="p-3 pointer-events-auto" />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Time *</Label>
                    <Input type="time" value={interviewTime} onChange={e => setInterviewTime(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Duration (min)</Label>
                    <Select value={interviewDuration} onValueChange={setInterviewDuration}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="15">15 min</SelectItem>
                        <SelectItem value="30">30 min</SelectItem>
                        <SelectItem value="45">45 min</SelectItem>
                        <SelectItem value="60">1 hour</SelectItem>
                        <SelectItem value="90">1.5 hours</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input placeholder="e.g. Office, Kinshasa" value={interviewLocation} onChange={e => setInterviewLocation(e.target.value)} />
                </div>

                <div className="space-y-2">
                  <Label>Meeting Link</Label>
                  <Input placeholder="https://zoom.us/..." value={interviewLink} onChange={e => setInterviewLink(e.target.value)} />
                </div>

                <div className="space-y-2">
                  <Label>Notes for applicant</Label>
                  <Textarea placeholder="Anything the applicant should know or prepare..." value={interviewNotes} onChange={e => setInterviewNotes(e.target.value)} />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowInterviewDialog(false); resetInterviewForm(); }}>Cancel</Button>
              <Button onClick={scheduleInterview} disabled={!interviewDate || schedulingInterview}>
                {schedulingInterview ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                Schedule & Notify
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Send Offer Dialog */}
        <Dialog open={showOfferDialog} onOpenChange={(open) => { if (!open) { setShowOfferDialog(false); resetOfferForm(); } }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Gift className="h-5 w-5 text-success" /> Send Offer</DialogTitle>
            </DialogHeader>
            {offerApp && (
              <div className="space-y-4 mt-2">
                <p className="text-sm text-muted-foreground">
                  Sending offer to <strong>{getApplicantName(offerApp)}</strong> for <strong>{offerApp.opportunity?.title}</strong>
                </p>

                <div className="space-y-2">
                  <Label>Offer Message *</Label>
                  <Textarea placeholder="Congratulations! We are pleased to offer you..." value={offerMessage}
                    onChange={e => setOfferMessage(e.target.value)} className="min-h-[100px]" />
                </div>

                <div className="space-y-2">
                  <Label>Salary Range</Label>
                  <Input placeholder="e.g. $60,000 - $80,000 / year" value={offerSalary} onChange={e => setOfferSalary(e.target.value)} />
                </div>

                <div className="space-y-2">
                  <Label>Proposed Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !offerStartDate && "text-muted-foreground")}>
                        <CalendarDays className="mr-2 h-4 w-4" />
                        {offerStartDate ? format(offerStartDate, 'PPP') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={offerStartDate} onSelect={setOfferStartDate}
                        disabled={(date) => date < new Date()} initialFocus className="p-3 pointer-events-auto" />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>Conditions / Additional Notes</Label>
                  <Textarea placeholder="Any conditions, benefits, or additional details..." value={offerConditions}
                    onChange={e => setOfferConditions(e.target.value)} />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowOfferDialog(false); resetOfferForm(); }}>Cancel</Button>
              <Button className="bg-success hover:bg-success/90 text-success-foreground" onClick={sendOffer} disabled={!offerMessage || sendingOffer}>
                {sendingOffer ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Gift className="h-4 w-4 mr-2" />}
                Send Offer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
